import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import WebinarCard from "@/components/WebinarCard";
import TopicPicker, { TOPICS } from "@/components/TopicPicker";
import LocationBadge from "@/components/LocationBadge";
import { fmtMoney } from "@/lib/format";
import { ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const STEPS = [
  ["Choose a webinar", "Pick your industry — we surface the next live session in your local time."],
  ["Reserve your seat", "Register in seconds. No calendars, no timezone maths."],
  ["Attend the session", "Join a focused 45-minute live masterclass."],
  ["Unlock the special offer", "Get an exclusive webinar-only bundle price."],
  ["Start learning", "Access courses instantly and earn your certificate."],
];

export default function Home() {
  const { location, topic, selectTopic } = useApp();
  const navigate = useNavigate();
  const [active, setActive] = useState(topic || "ai");
  const [webinar, setWebinar] = useState(null);
  const [wLoading, setWLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);

  useEffect(() => { api.logEvent("landing.visit"); }, []);

  useEffect(() => {
    setWLoading(true);
    api.nextWebinar(active).then((d) => setWebinar(d.available ? d.webinar : null)).finally(() => setWLoading(false));
  }, [active]);

  useEffect(() => {
    if (!location) return;
    api.courses(null, location.country).then((d) => setCourses(d.courses.slice(0, 6)));
    api.bundles(location.country).then((d) => setBundles(d.bundles));
  }, [location]);

  const sym = location?.symbol || "₹";

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden mesh-bg noise">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-asf-app/40 via-asf-app/70 to-asf-app" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 sm:pt-28">
          <div className="asf-fade-up max-w-3xl">
            <div className="asf-overline mb-5 flex items-center gap-2"><Sparkles className="h-4 w-4" /> Live webinars · Practical courses</div>
            <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Learn AI.<br />Master Industries.<br /><span className="text-asf-orange">Build Your Future.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mt-6 max-w-xl">
              Live webinars. Practical courses. Industry-focused learning across AI, Real Estate, Insurance & Loans.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/courses" data-testid="hero-explore-courses" className="btn-primary px-8 py-4 inline-flex items-center gap-2">
                Explore Courses <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/webinars" data-testid="hero-join-webinar" className="btn-secondary px-8 py-4">Join a Webinar</Link>
            </div>
            <div className="mt-8"><LocationBadge /></div>
          </div>
        </div>
      </section>

      {/* WHAT DO YOU WANT TO LEARN + LIVE LEARNING */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="asf-overline mb-3">Smart concierge</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-8">What do you want to learn?</h2>
        <TopicPicker active={active} onSelect={setActive} />

        <div className="mt-10">
          <div className="asf-overline mb-3 flex items-center gap-2">Live Learning · your next webinar</div>
          {wLoading ? (
            <div className="rounded-3xl bg-slate-100 h-56 animate-pulse" />
          ) : webinar ? (
            <WebinarCard webinar={webinar} />
          ) : (
            <div className="asf-card p-8 text-center">
              <p className="text-slate-600">New {active} webinar sessions are being scheduled.</p>
              <Link to={`/webinars/${active}`} className="btn-secondary inline-block mt-4 px-6 py-3">Notify Me</Link>
            </div>
          )}
        </div>
      </section>

      {/* CHOOSE YOUR INDUSTRY */}
      <section className="max-w-7xl mx-auto px-6 py-10">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">Choose your industry</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {TOPICS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.slug} data-testid={`industry-${t.slug}`}
                onClick={() => { selectTopic(t.slug); navigate(`/courses/${t.slug}`); }}
                className="asf-card p-8 text-left">
                <Icon className="h-8 w-8 text-asf-orange mb-4" />
                <div className="font-display text-xl font-semibold">{t.label}</div>
                <div className="text-sm text-slate-500 mt-1">{t.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* POPULAR COURSES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Popular courses</h2>
          <Link to="/courses" className="text-sm text-asf-orange font-semibold flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => <CourseCard key={c.slug} course={c} symbol={sym} />)}
        </div>
      </section>

      {/* WEBINAR-ONLY OFFERS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="asf-overline mb-3">Webinar-only</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-8">Exclusive course bundle offers</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {bundles.map((b) => (
            <Link key={b.slug} to={`/webinars/${b.industry}`} data-testid={`bundle-${b.slug}`} className="asf-card p-6 flex flex-col">
              <div className="asf-overline">{b.title.replace(" Complete Bundle", "")}</div>
              <div className="font-display text-lg font-semibold mt-2">{b.title}</div>
              <div className="mt-4">
                <span className="text-slate-500 line-through text-sm">{fmtMoney(sym, b.regular_price)}</span>
                <div className="font-display text-3xl font-extrabold text-asf-orange">{fmtMoney(sym, b.offer_price)}</div>
                <div className="text-xs text-slate-500">webinar-exclusive price</div>
              </div>
              <span className="mt-auto pt-4 text-sm text-slate-900 font-semibold flex items-center gap-1">Unlock via webinar <ArrowRight className="h-4 w-4" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">How it works</h2>
        <div className="grid md:grid-cols-5 gap-4">
          {STEPS.map(([title, desc], i) => (
            <div key={i} className="asf-card p-6">
              <div className="font-mono text-asf-orange text-sm">0{i + 1}</div>
              <div className="font-display text-lg font-semibold mt-3">{title}</div>
              <p className="text-sm text-slate-500 mt-2">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="relative rounded-3xl p-[1px] bg-gradient-to-r from-asf-orange/70 to-blue-500/40">
          <div className="rounded-3xl bg-asf-surface p-10 sm:p-16 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Find your next skill</h2>
            <p className="text-slate-600 mt-4 max-w-lg mx-auto">Join a live webinar today and unlock exclusive learning offers.</p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <Link to="/webinars" className="btn-primary px-8 py-4">Join a Webinar</Link>
              <Link to="/courses" className="btn-secondary px-8 py-4">Browse Courses</Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center mt-8 text-sm text-slate-500">
              {["Certificates included", "Lifetime access", "45-min live sessions"].map((f) => (
                <span key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-asf-orange" /> {f}</span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
